<template>
  <div class="app-wrapper">
    <!-- 侧边栏 -->
    <div class="sidebar-container">
      <div class="logo">
        <img src="@/assets/img/logo.svg" alt="Logo" />
        <span>认证管理系统</span>
      </div>
      <el-menu
        :default-active="activeMenu"
        background-color="#304156"
        text-color="#fff"
        active-text-color="#ffd04b"
        router
      >
        <template v-for="item in routes">
          <el-menu-item v-if="!item.hidden && !item.children" :key="'menu-'+item.path" :index="item.path">
            <i :class="item.meta.icon || 'el-icon-s-help'"></i>
            <span slot="title">{{ item.meta.title }}</span>
          </el-menu-item>
          
          <el-submenu v-else-if="!item.hidden && item.children" :key="'submenu-'+item.path" :index="item.path">
            <template slot="title">
              <i :class="item.meta.icon || 'el-icon-folder'"></i>
              <span>{{ item.meta.title }}</span>
            </template>
            
            <el-menu-item 
              v-for="child in item.children.filter(c => !c.hidden)" 
              :key="item.path + '-' + child.path"
              :index="item.path + '/' + child.path"
            >
              <span>{{ child.meta.title }}</span>
            </el-menu-item>
          </el-submenu>
        </template>
      </el-menu>
    </div>
    
    <!-- 主内容区 -->
    <div class="main-container">
      <!-- 顶部导航 -->
      <div class="navbar">
        <div class="right-menu">
          <!-- 消息中心入口 -->
          <el-tooltip content="消息中心" placement="bottom">
            <div class="message-icon-container" @click="toMessages">
              <div class="message-icon">
                <el-badge :value="unreadCount" :max="99" class="item" v-if="unreadCount > 0">
                  <i class="el-icon-bell"></i>
                </el-badge>
                <i class="el-icon-bell" v-else></i>
              </div>
            </div>
          </el-tooltip>
          
          <el-dropdown trigger="click">
            <div class="avatar-wrapper">
              <img src="@/assets/images/3ea6beec64369c2642b92c6726f1epng.png" class="user-avatar" />
              <i class="el-icon-caret-bottom"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item>个人中心</el-dropdown-item>
              <el-dropdown-item divided @click.native="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
      
      <!-- 内容区 -->
      <div class="app-main">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import { getUnreadMessageCount } from '@/api/task';
import axios from 'axios';

export default {
  name: 'Layout',
  data() {
    return {
      unreadCount: 0
    }
  },
  computed: {
    routes() {
      // 从路由配置中获取路由
      const routes = this.$router.options.routes.filter(route => 
        route.path === '/auth' || route.path === '/taskAdmin' || route.path === '/bussin'
      );
      
      // 获取用户菜单权限，从localStorage中获取
      const userMenuIds = this.getUserMenuPermissions();
      console.log('用户菜单权限ID列表:', userMenuIds);
      
      // 判断用户角色
      const isCreator = this.isCreator();
      const isAdmin = this.isAdmin();
      
      // 管理员或创建者显示全部菜单
      if (isAdmin || isCreator) {
        console.log('当前用户是管理员或创建者，展示所有菜单');
        return routes;
      }
      
      // 如果没有获取到用户菜单权限，先尝试加载
      if (!userMenuIds || userMenuIds.length === 0) {
        console.log('未找到用户菜单权限，尝试重新加载');
        this.loadUserMenuPermissions();
        // 等待权限加载完成，先返回空菜单
        return [];
      }
      
      // 过滤路由，只保留用户有权限的路由
      return routes.map(route => {
        // 复制一份路由对象
        const newRoute = { ...route };
        
        // 如果有子路由，过滤子路由
        if (newRoute.children && newRoute.children.length > 0) {
          // 过滤子路由，只保留用户有权限的子路由
          newRoute.children = newRoute.children.filter(child => {
            // 检查是否有权限访问此菜单
            return this.hasPermission(child, userMenuIds);
          });
        }
        
        // 如果过滤后没有子路由，则隐藏整个父级菜单
        if (newRoute.children && newRoute.children.length === 0) {
          newRoute.hidden = true;
        }
        
        return newRoute;
      }).filter(route => !route.hidden); // 过滤掉隐藏的路由
    },
    activeMenu() {
      const route = this.$route;
      const { meta, path } = route;
      // 优先使用meta.activeMenu
      if (meta.activeMenu) {
        return meta.activeMenu;
      }
      return path;
    }
  },
  created() {
    // 获取未读消息数量
    this.getUnreadMessageCount();
    
    // 加载用户的菜单权限
    this.loadUserMenuPermissions();
    
    // 监听菜单权限更新事件
    window.addEventListener('menu-permissions-updated', this.handleMenuPermissionsUpdated);
  },
  beforeDestroy() {
    // 移除事件监听器
    window.removeEventListener('menu-permissions-updated', this.handleMenuPermissionsUpdated);
  },
  methods: {
    // 处理菜单权限更新事件
    handleMenuPermissionsUpdated() {
      console.log('菜单权限已更新，重新计算可见菜单');
      // 强制重新计算routes计算属性
      this.$forceUpdate();
    },
    
    // 从localStorage获取用户菜单权限
    getUserMenuPermissions() {
      // 优先从localStorage获取菜单ID权限
      const userMenuIdsStr = localStorage.getItem('userMenuIds');
      if (userMenuIdsStr) {
        try {
          return JSON.parse(userMenuIdsStr);
        } catch (e) {
          console.error('解析用户菜单权限失败:', e);
          return [];
        }
      }
      return [];
    },
    
    // 加载用户菜单权限
    loadUserMenuPermissions() {
      // 从后端获取用户信息，包括菜单权限
      axios.get('/zr-admin/getInfo', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('logintoken'),
          'X-Client-Id': 'zr_platform',
          'X-User-Id': this.getCurrentUserId() || ''
        }
      })
        .then(response => {
          if (response.data && response.data.code === 200) {
            const data = response.data.data;
            if (data.userMenuIds && data.userMenuIds.length > 0) {
              // 保存到localStorage
              localStorage.setItem('userMenuIds', JSON.stringify(data.userMenuIds));
              // 更新菜单显示
              this.$forceUpdate();
            }
          }
        })
        .catch(error => {
          console.error('获取用户菜单权限失败:', error);
        });
    },
    
    // 检查是否是创建者
    isCreator() {
      const userdata = localStorage.getItem('userdata');
      if (userdata) {
        try {
          const userData = JSON.parse(userdata);
          return userData.userType === 'creator' || userData.userType === '1';
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    
    // 检查是否是管理员
    isAdmin() {
      const userdata = localStorage.getItem('userdata');
      if (userdata) {
        try {
          const userData = JSON.parse(userdata);
          return userData.userType === 'admin' || userData.userType === '0';
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    
    // 检查是否有权限访问菜单
    hasPermission(route, menuIds) {
      // 如果路由配置了hidden属性为true，则不显示
      if (route.hidden) return false;
      
      // 如果没有配置meta或者meta.menuId，则默认有权限
      if (!route.meta || !route.meta.menuId) return true;
      
      // 检查菜单ID是否在用户权限列表中
      const menuId = route.meta.menuId;
      return menuIds.includes(menuId) || menuIds.includes(String(menuId));
    },
    
    // 获取未读消息数量
    getUnreadMessageCount() {
      // 调用API获取未读消息数量
      getUnreadMessageCount()
        .then(res => {
          if (res.code === 200) {
            this.unreadCount = res.data || 0;
          } else {
            console.error('获取未读消息数量失败:', res.msg);
          }
        })
        .catch(error => {
          console.error('获取未读消息数量失败:', error);
        });
    },
    // 跳转到消息中心
    toMessages() {
      console.log('开始跳转到消息中心，路径: /auth/messages');
      try {
        this.$router.push('/auth/messages').catch(err => {
          console.error('路由跳转失败:', err);
          
          // 如果是重复导航错误，可以忽略
          if (err.name !== 'NavigationDuplicated') {
            // 尝试使用location.href作为备选方案
            window.location.href = this.$router.resolve('/auth/messages').href;
          }
        });
      } catch (error) {
        console.error('跳转消息中心出错:', error);
        // 备选方案
        window.location.href = this.$router.resolve('/auth/messages').href;
      }
    },
    logout() {
      // 清除所有本地存储
      localStorage.removeItem('token');
      localStorage.removeItem('logintoken');
      localStorage.removeItem('userdata');
      localStorage.removeItem('company');
      localStorage.removeItem('sysRolePower');
      localStorage.removeItem('userMenuPermissions');
      
      // 跳转到登录页面
      this.$router.push('/login');
    },
    // 为角色加载菜单权限
    loadRoleMenuPermissions(roleId) {
      console.log(`开始获取角色[${roleId}]的菜单权限`);
      
      return new Promise((resolve, reject) => {
        // 带上时间戳参数防止浏览器缓存
        const timestamp = new Date().getTime();
        // 使用axios而不是this.$http
        axios.get(`/zr-admin/systemMenu/roleMenuTreeselect/${roleId}?_t=${timestamp}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('logintoken'),
            'X-Client-Id': 'zr_platform',
            'X-User-Id': this.getCurrentUserId() || ''
          }
        })
        .then(response => {
          if (response.data && response.data.code === 200) {
            console.log(`角色[${roleId}]菜单权限获取成功:`, response.data.data);
            resolve(response.data.data || []);
          } else {
            console.warn(`获取角色[${roleId}]菜单权限失败:`, response.data ? response.data.msg : '未知错误');
            resolve([]);
          }
        })
        .catch(error => {
          console.error(`获取角色[${roleId}]菜单权限发生错误:`, error);
          resolve([]);
        });
      });
    },
    
    // 获取当前用户ID
    getCurrentUserId() {
      const userdata = localStorage.getItem('userdata');
      if (userdata) {
        try {
          const userData = JSON.parse(userdata);
          return userData.userId;
        } catch (e) {
          return '';
        }
      }
      return '';
    }
  }
};
</script>

<style scoped>
.app-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
}

.sidebar-container {
  width: 210px;
  min-height: 100vh;
  background-color: #304156;
  transition: width 0.28s;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
}

.logo {
  height: 50px;
  padding: 10px;
  display: flex;
  align-items: center;
  background-color: #2b3649;
}

.logo img {
  width: 32px;
  height: 32px;
  margin-right: 10px;
}

.logo span {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.navbar {
  height: 50px;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  background-color: #fff;
}

.right-menu {
  display: flex;
  align-items: center;
}

.avatar-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 15px;
  margin-right: 5px;
}

.app-main {
  flex: 1;
  padding: 20px;
  overflow: auto;
  background-color: #f0f2f5;
}

.message-icon-container {
  margin-right: 20px;
  font-size: 24px;
  cursor: pointer;
  color: #409EFF;
  transition: color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 10px;
}

.message-icon-container:hover {
  color: #66b1ff;
  background-color: #f0f7ff;
}

.message-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.el-badge__content.is-fixed {
  top: 8px;
  right: 8px;
}
</style> 


// WEBPACK FOOTER //
// src/layout/index.vue